import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useLeadOptions } from "../lib/useLeadOptions";
import { Navigate } from "react-router-dom";
import {
  GoogleLogo, MetaLogo, ArrowClockwise, CheckCircle, XCircle,
  Warning, ArrowsClockwise, Info,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Offline conversion upload settings + history.
 *
 * Sends closed deals back to Google Ads and Meta so their bidding optimises for
 * patients who actually paid rather than for cheap form fills. Everything is
 * keyed on the click IDs already stored per lead.
 */
export default function Conversions() {
  const { user } = useAuth();
  const { statuses } = useLeadOptions();
  const [cfg, setCfg] = useState(null);
  const [creds, setCreds] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [uploads, setUploads] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backfill, setBackfill] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [c, cr, r, u] = await Promise.all([
        api.get("/conversions/config"),
        api.get("/credentials"),
        api.get("/conversions/readiness"),
        api.get("/conversions/uploads", { params: { limit: 50 } }),
      ]);
      setCfg(c.data);
      setCreds(cr.data || []);
      setReadiness(r.data);
      setUploads(u.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (user && !isAdmin) return <Navigate to="/" replace />;
  if (!cfg) {
    return (
      <div className="min-h-screen">
        <PageHeader eyebrow="Attribution" title="Offline conversions" />
        <div className="px-8 py-10 text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const c = cfg.config;
  const present = cfg.credentials_present || {};

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/conversions/config", body);
      setCfg(data);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runBackfill = async (apply) => {
    try {
      const { data } = await api.post("/conversions/backfill", { apply, limit: 500 });
      setBackfill(data);
      if (apply) {
        toast.success(`Uploaded ${data.uploaded}, failed ${data.failed}`);
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const retry = async (leadId) => {
    try {
      await api.post(`/conversions/leads/${leadId}/upload`);
      toast.success("Re-sent");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const credOptions = (service) =>
    creds.filter((x) => !service || x.service === service);

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Attribution"
        title="Offline conversions"
        description="Send closed deals back to Google Ads and Meta so their bidding optimises for patients who actually pay, not for cheap form fills."
      />

      <div className="px-8 pb-12 space-y-6">
        {/* Readiness */}
        {readiness && (
          <div className="border border-border">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <Info size={14} />
              <h3 className="font-display text-lg font-semibold tracking-tight">
                What can be uploaded
              </h3>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
              {[
                ["Closed leads", readiness.converted_leads],
                ["Google-ready", readiness.google_ready],
                ["Meta-ready", readiness.meta_ready],
                ["No identifier", readiness.no_identifier],
              ].map(([label, value]) => (
                <div key={label} className="bg-white p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {label}
                  </div>
                  <div className="font-display text-3xl font-bold mt-2 tracking-tight">
                    {(value ?? 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
            {readiness.no_identifier > 0 && (
              <div className="px-5 py-3 border-t border-border bg-amber-500/5 text-xs text-amber-800">
                <Warning size={12} className="inline mr-1" />
                {readiness.no_identifier} closed lead(s) carry no click ID, email or
                phone and can never be uploaded. This caps how much of your ad spend
                can be optimised — the usual cause is a landing page that drops the
                query string before the form posts.
                {Object.keys(readiness.blocked_reasons || {}).length > 0 && (
                  <ul className="mt-1.5 ml-4 list-disc">
                    {Object.entries(readiness.blocked_reasons).map(([reason, n]) => (
                      <li key={reason}>
                        {reason} — {n}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* Trigger + value */}
        <Panel title="When to upload">
          <div className="space-y-4">
            <div>
              <Label>Statuses that count as a conversion</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {(statuses || []).map((s) => {
                  const on = (c.trigger_statuses || []).includes(s.id);
                  return (
                    <button
                      key={s.id}
                      disabled={saving}
                      onClick={() =>
                        patch({
                          trigger_statuses: on
                            ? c.trigger_statuses.filter((x) => x !== s.id)
                            : [...(c.trigger_statuses || []), s.id],
                        })
                      }
                      className={`px-2.5 py-1 text-[11px] border transition-colors ${
                        on
                          ? "border-primary bg-primary/10 text-primary font-semibold"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Conversion value from</Label>
                <select
                  value={c.value_source}
                  onChange={(e) => patch({ value_source: e.target.value })}
                  className="w-full mt-1 text-sm border border-border px-2 py-1.5 bg-white"
                >
                  {(cfg.value_sources || []).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Fallback value</Label>
                <input
                  type="number"
                  defaultValue={c.fixed_value}
                  onBlur={(e) => patch({ fixed_value: Number(e.target.value) })}
                  className="w-full mt-1 text-sm border border-border px-2 py-1.5"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Used when the lead has no recorded revenue. Uploading a patient as
                  worth 0 teaches the algorithm they were worthless.
                </p>
              </div>
              <div>
                <Label>Currency</Label>
                <input
                  defaultValue={c.currency}
                  onBlur={(e) => patch({ currency: e.target.value })}
                  className="w-full mt-1 text-sm border border-border px-2 py-1.5 uppercase"
                />
              </div>
            </div>
          </div>
        </Panel>

        {/* Google */}
        <Panel
          title="Google Ads"
          icon={<GoogleLogo size={16} weight="fill" className="text-[#4285F4]" />}
          enabled={c.google?.enabled}
          onToggle={(v) => patch({ google: { enabled: v } })}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="Customer ID (10 digits, no dashes)"
              value={c.google?.customer_id}
              onSave={(v) => patch({ google: { customer_id: v } })} />
            <TextField label="Login / manager customer ID (optional)"
              value={c.google?.login_customer_id}
              onSave={(v) => patch({ google: { login_customer_id: v } })} />
            <TextField label="Conversion action resource name" wide
              placeholder="customers/1234567890/conversionActions/999"
              value={c.google?.conversion_action}
              onSave={(v) => patch({ google: { conversion_action: v } })} />
            <TextField label="OAuth client ID"
              value={c.google?.client_id}
              onSave={(v) => patch({ google: { client_id: v } })} />
            <CredField label="Developer token" creds={credOptions("google_ads")}
              value={c.google?.developer_token_cred_id} ok={present.google_developer_token}
              onSave={(v) => patch({ google: { developer_token_cred_id: v } })} />
            <CredField label="OAuth client secret" creds={credOptions("google_ads")}
              value={c.google?.client_secret_cred_id} ok={present.google_client_secret}
              onSave={(v) => patch({ google: { client_secret_cred_id: v } })} />
            <CredField label="OAuth refresh token" creds={credOptions("google_ads")}
              value={c.google?.refresh_token_cred_id} ok={present.google_refresh_token}
              onSave={(v) => patch({ google: { refresh_token_cred_id: v } })} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Uploads use gclid, or gbraid / wbraid when Google auto-tagged an iOS click.
            Google accepts conversions up to 90 days old.
          </p>
        </Panel>

        {/* Meta */}
        <Panel
          title="Meta (Conversions API)"
          icon={<MetaLogo size={16} weight="fill" className="text-[#1877F2]" />}
          enabled={c.meta?.enabled}
          onToggle={(v) => patch({ meta: { enabled: v } })}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField label="Dataset / Pixel ID"
              value={c.meta?.dataset_id}
              onSave={(v) => patch({ meta: { dataset_id: v } })} />
            <TextField label="Event name"
              value={c.meta?.event_name}
              onSave={(v) => patch({ meta: { event_name: v } })} />
            <CredField label="Access token" creds={credOptions("meta_ads")}
              value={c.meta?.access_token_cred_id} ok={present.meta_access_token}
              onSave={(v) => patch({ meta: { access_token_cred_id: v } })} />
            <TextField label="Test event code (while validating)"
              value={c.meta?.test_event_code}
              onSave={(v) => patch({ meta: { test_event_code: v } })} />
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Email and phone are sent as SHA-256 hashes only — no personal data leaves
            the CRM in plaintext. Meta accepts conversions up to 7 days old.
          </p>
        </Panel>

        {/* Backfill */}
        <Panel title="Backfill past conversions">
          <div className="flex items-center gap-2">
            <button onClick={() => runBackfill(false)}
              className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] border border-border hover:bg-secondary">
              Dry run
            </button>
            <button onClick={() => runBackfill(true)}
              disabled={!backfill || !backfill.would_upload}
              className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] border border-primary bg-primary text-primary-foreground disabled:opacity-40">
              Upload {backfill?.would_upload ? `(${backfill.would_upload})` : ""}
            </button>
          </div>
          {backfill && (
            <div className="mt-3 text-xs text-muted-foreground">
              {backfill.dry_run ? (
                <>
                  Would upload <strong>{backfill.would_upload}</strong>, skip{" "}
                  <strong>{backfill.skipped}</strong>.
                </>
              ) : (
                <>
                  Uploaded <strong>{backfill.uploaded}</strong>, failed{" "}
                  <strong>{backfill.failed}</strong>.
                </>
              )}
            </div>
          )}
        </Panel>

        {/* Upload log */}
        <div className="border border-border">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold tracking-tight">
              Upload history
            </h3>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              {uploads && (
                <span>
                  {uploads.succeeded} ok · {uploads.failed} failed
                </span>
              )}
              <button onClick={load} className="p-1.5 hover:bg-secondary">
                <ArrowClockwise size={14} />
              </button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {(uploads?.items || []).length === 0 && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">
                Nothing uploaded yet.
              </div>
            )}
            {(uploads?.items || []).map((u) => (
              <div key={u.id}
                className="px-5 py-3 border-b border-border last:border-b-0 flex items-center gap-3 text-sm">
                {u.success ? (
                  <CheckCircle size={16} weight="fill" className="text-green-600 shrink-0" />
                ) : (
                  <XCircle size={16} weight="fill" className="text-destructive shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    <span className="font-semibold">{u.customer_name || u.lead_id}</span>
                    <span className="text-muted-foreground"> · {u.destination}</span>
                    <span className="text-muted-foreground"> · {u.click_id_field}</span>
                  </div>
                  {u.error && (
                    <div className="text-[11px] text-destructive truncate" title={u.error}>
                      {u.error}
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {u.value ? `${u.currency} ${Number(u.value).toLocaleString()}` : "—"}
                </div>
                {!u.success && (
                  <button onClick={() => retry(u.lead_id)} title="Retry"
                    className="p-1.5 hover:bg-secondary shrink-0">
                    <ArrowsClockwise size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, icon, enabled, onToggle, children }) {
  return (
    <div className="border border-border bg-white">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
        </div>
        {onToggle && (
          <button
            onClick={() => onToggle(!enabled)}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-bold border ${
              enabled
                ? "border-green-600 bg-green-600/10 text-green-700"
                : "border-border text-muted-foreground"
            }`}
          >
            {enabled ? "On" : "Off"}
          </button>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
      {children}
    </label>
  );
}

function TextField({ label, value, onSave, placeholder, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      <input
        defaultValue={value || ""}
        placeholder={placeholder}
        onBlur={(e) => e.target.value !== (value || "") && onSave(e.target.value)}
        className="w-full mt-1 text-sm border border-border px-2 py-1.5 font-mono"
      />
    </div>
  );
}

/** Credential picker — stores a vault ID, never a secret value. */
function CredField({ label, creds, value, ok, onSave }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex items-center gap-2 mt-1">
        <select
          value={value || ""}
          onChange={(e) => onSave(e.target.value)}
          className="flex-1 text-sm border border-border px-2 py-1.5 bg-white"
        >
          <option value="">— not set —</option>
          {creds.map((cr) => (
            <option key={cr.id} value={cr.id}>
              {cr.name} ({cr.service})
            </option>
          ))}
        </select>
        {value &&
          (ok ? (
            <CheckCircle size={16} weight="fill" className="text-green-600 shrink-0"
              title="Resolves to a stored secret" />
          ) : (
            <Warning size={16} weight="fill" className="text-amber-500 shrink-0"
              title="This credential ID no longer resolves — it may have been deleted from the vault" />
          ))}
      </div>
    </div>
  );
}
